import React from 'react';
import { Chart } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const formatCurrency = (value) => {
    if (value >= 1000000) {
        const millions = value / 1000000;
        // If it's a whole number or ends in .0, show no decimals
        if (millions % 1 === 0 || millions.toFixed(1).endsWith('.0')) {
            return `$${Math.floor(millions)}M`;
        }
        // Otherwise show one decimal
        return `$${millions.toFixed(1)}M`;
    }
    return `$${Math.round(value / 1000)}K`;
};

const HomeValueVsRequiredIncomeChart = ({ futureIncomeRequired, futureHomeValues, projectedFutureIncome, chartTitle }) => {
    const yearLabels = ['Today', ...Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`)];

    // Find the minimum values for both axes
    const minHomeValue = Math.min(...futureHomeValues);
    const homeValueFloor = Math.floor(minHomeValue / 250000) * 250000;

    // Get all income values including projected income
    const allIncomeValues = [
        ...futureIncomeRequired.flatMap(level => level.requiredIncome),
        ...projectedFutureIncome
    ];
    const minIncomeValue = Math.min(...allIncomeValues);
    const incomeFloor = Math.floor(minIncomeValue / 100000) * 100000;

    return (
        <Chart 
            type="bar"
            data={{
                labels: yearLabels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Future Home Value',
                        data: futureHomeValues,
                        backgroundColor: 'rgba(200, 200, 200, 0.2)',
                        borderColor: 'rgba(200, 200, 200, 0.6)',
                        borderWidth: 1,
                        yAxisID: 'y-axis-2',
                        pointStyle: 'rect',
                        legendColor: 'rgba(200, 200, 200, 0.6)'
                    },
                    // Add projected income line
                    {
                        type: 'line',
                        label: 'Projected Household Income in the Area',
                        data: projectedFutureIncome,
                        borderColor: '#2196F3', // Blue color for projected income
                        backgroundColor: '#2196F3',
                        tension: 0.1,
                        fill: false,
                        borderDash: [5, 5], // Make the line dashed
                        pointStyle: 'line',
                        pointRadius: 1,
                        pointHoverRadius: 5,
                        pointBorderColor: 'transparent',
                        pointBackgroundColor: 'transparent',
                        legendColor: '#2196F3'
                    },
                    ...futureIncomeRequired.map((incomeLevel) => ({
                        type: 'line',
                        label: `Buyer Spends ${incomeLevel.percentage} of Income`,
                        data: incomeLevel.requiredIncome,
                        borderColor: incomeLevel.level === 'best' ? '#4CAF50' :
                                   incomeLevel.level === 'good' ? '#8BC34A' :
                                   incomeLevel.level === 'ok' ? '#FFC107' : '#F44336',
                        backgroundColor: incomeLevel.level === 'best' ? '#4CAF50' :
                                       incomeLevel.level === 'good' ? '#8BC34A' :
                                       incomeLevel.level === 'ok' ? '#FFC107' : '#F44336',
                        tension: 0.1,
                        fill: false,
                        pointStyle: 'line',
                        pointRadius: 1,
                        pointHoverRadius: 5,
                        pointBorderColor: 'transparent',
                        pointBackgroundColor: 'transparent',
                        legendColor: incomeLevel.level === 'best' ? '#4CAF50' :
                                   incomeLevel.level === 'good' ? '#8BC34A' :
                                   incomeLevel.level === 'ok' ? '#FFC107' : '#F44336'
                    }))
                ]
            }}
            options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                plugins: {
                    title: {
                        display: true,
                        text: chartTitle,
                        font: {
                            size: 16,
                            weight: 'bold'
                        },
                        padding: {
                            bottom: 20
                        }
                    },
                    legend: {
                        position: 'bottom',
                        display: true,
                        labels: {
                            usePointStyle: true,
                            generateLabels: (chart) => {
                                const datasets = chart.data.datasets;
                                return datasets.map((dataset, i) => ({
                                    text: dataset.label,
                                    fillStyle: dataset.legendColor,
                                    strokeStyle: dataset.legendColor,
                                    lineWidth: 2,
                                    hidden: !chart.isDatasetVisible(i),
                                    index: i,
                                    pointStyle: dataset.pointStyle,
                                    borderDash: dataset.borderDash
                                }));
                            }
                        }
                    },
                    datalabels: {
                        display: false
                    },
                    tooltip: {
                        enabled: true,
                        usePointStyle: true,
                        callbacks: {
                            labelColor: function(context) {
                                return {
                                    borderColor: context.dataset.borderColor,
                                    backgroundColor: context.dataset.borderColor
                                };
                            },
                            label: (context) => {
                                const value = context.raw;
                                const label = context.dataset.label;
                                return `${label}: ${formatCurrency(value)}`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Required Income ($)'
                        },
                        min: incomeFloor, // Set minimum to nearest $100K below lowest value
                        ticks: {
                            callback: (value) => formatCurrency(value)
                        }
                    },
                    'y-axis-2': {
                        type: 'linear',
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Home Value ($)'
                        },
                        min: homeValueFloor, // Set minimum to nearest $250K below lowest value
                        ticks: {
                            callback: (value) => formatCurrency(value)
                        },
                        grid: {
                            drawOnChartArea: false
                        }
                    }
                }
            }}
        />
    );
};

export default HomeValueVsRequiredIncomeChart;
